import React, { useState, useEffect, useCallback } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  message,
  DatePicker,
  Space,
  Checkbox
} from 'antd'
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons'
import { ColorPicker } from 'antd'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import api from '../utils/api'
import styled from 'styled-components'
import { fromPicker } from '../utils/timezone'

dayjs.extend(isBetween)

const ButtonGroup = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
`
const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`

const EmployeeExperience = ({ employeeId, businessId }) => {
  const [form] = Form.useForm()
  const [experiences, setExperiences] = useState([])
  const [currentJobs, setCurrentJobs] = useState({})

  const fetchExperiences = useCallback(async () => {
    try {
      const response = await api.get(
        `/api/employees/${businessId}/${employeeId}/experiences`
      )
      const formattedExperiences = (response.data.experiences || []).map(
        (exp) => {
          const formattedExp = {
            ...exp,
            startDate: exp.startDate ? dayjs(exp.startDate) : null,
            endDate: exp.endDate ? dayjs(exp.endDate) : null
          }
          setCurrentJobs((prev) => ({ ...prev, [exp._id]: exp.isCurrent }))
          return formattedExp
        }
      )
      setExperiences(formattedExperiences)
      form.setFieldsValue({ experiences: formattedExperiences })
    } catch (error) {
      console.error('獲取工作經歷時出錯：', error)
      setExperiences([])
    }
  }, [businessId, employeeId, form])

  useEffect(() => {
    fetchExperiences()
  }, [fetchExperiences])

  const validateExperiences = (experiences) => {
    if (experiences.length <= 1) return true

    const sortedExperiences = [...experiences].sort((a, b) =>
      dayjs(b.startDate).diff(dayjs(a.startDate))
    )

    for (let i = 0; i < sortedExperiences.length - 1; i++) {
      const current = sortedExperiences[i]
      const next = sortedExperiences[i + 1]

      if (current.endDate && next.endDate) {
        if (dayjs(current.startDate).isSameOrBefore(dayjs(next.endDate))) {
          return false
        }
      } else if (current.endDate && !next.endDate) {
        if (dayjs(current.startDate).isSameOrBefore(dayjs(next.startDate))) {
          return false
        }
      }
    }
    return true
  }

  const handleExperienceSubmit = async (values) => {
    if (!validateExperiences(values.experiences)) {
      message.error('工作經歷時間存在重疊或順序錯誤，請檢查並修正。')
      return
    }

    try {
      const processedExperiences = values.experiences.map((exp) => ({
        company: exp.company,
        title: exp.title,
        // fromPicker：使用者挑的年月要照原樣送，直接 toISOString() 會早 8 小時
        // 而落到前一天／前一個月，年資就算少一段
        startDate: exp.startDate ? fromPicker(exp.startDate).toISOString() : null,
        endDate: currentJobs[exp.id]
          ? null
          : exp.endDate
          ? fromPicker(exp.endDate).toISOString()
          : null,
        location: exp.location,
        color:
          typeof exp.color === 'string' ? exp.color : exp.color.toHexString(),
        isCurrent: !!currentJobs[exp.id]
      }))

      const payload = { experiences: processedExperiences }

      const response = await api.put(
        `/api/employees/${businessId}/${employeeId}/experiences`,
        payload
      )
      message.success('工作經歷保存成功')
      setExperiences(response.data.experiences || [])
    } catch (error) {
      console.error('保存工作經歷時出錯：', error)
      message.error('保存工作經歷失敗')
    }
  }

  return (
    <>
      {experiences.length === 0 ? (
        <div>
          <Button type='primary' onClick={() => setExperiences([{}])}>
            添加工作經歷
          </Button>
        </div>
      ) : (
        <Form
          form={form}
          onFinish={handleExperienceSubmit}
          initialValues={{ experiences }}
        >
          <Form.List name='experiences'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      border: '1px solid #f0f0f0',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px'
                    }}
                  >
                    <Row gutter={[16, 16]} align='middle'>
                      <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'company']}
                          rules={[
                            { required: true, message: '請輸入公司名稱' }
                          ]}
                        >
                          <Input placeholder='公司名稱' />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={5}>
                        <Form.Item
                          {...restField}
                          name={[name, 'title']}
                          rules={[{ required: true, message: '請輸入職位' }]}
                        >
                          <Input placeholder='職位' />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={16} lg={10}>
                        <Form.Item
                          {...restField}
                          name={[name, 'duration']}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                const startDate = getFieldValue([
                                  'experiences',
                                  name,
                                  'startDate'
                                ])
                                const endDate = getFieldValue([
                                  'experiences',
                                  name,
                                  'endDate'
                                ])
                                const isCurrentJob =
                                  currentJobs[experiences[name]?._id]

                                if (!startDate) {
                                  return Promise.reject(
                                    new Error('請選擇開始時間')
                                  )
                                }

                                if (!isCurrentJob && !endDate) {
                                  return Promise.reject(
                                    new Error('請選擇結束時間或勾選"至今"')
                                  )
                                }

                                if (
                                  endDate &&
                                  dayjs(startDate).isAfter(dayjs(endDate))
                                ) {
                                  return Promise.reject(
                                    new Error('開始時間不能晚於結束時間')
                                  )
                                }

                                const allExperiences =
                                  getFieldValue('experiences')
                                if (!validateExperiences(allExperiences)) {
                                  return Promise.reject(
                                    new Error('工作經歷時間存在重疊或順序錯誤')
                                  )
                                }

                                return Promise.resolve()
                              }
                            })
                          ]}
                        >
                          <Space style={{ width: '100%' }}>
                            <Space.Compact block>
                              <Form.Item name={[name, 'startDate']} noStyle>
                                <DatePicker
                                  picker='month'
                                  placeholder='開始時間'
                                  style={{ width: '100px' }}
                                  disabledDate={(current) => {
                                    const allExperiences = fields.map((f) => ({
                                      startDate: form.getFieldValue([
                                        'experiences',
                                        f.name,
                                        'startDate'
                                      ]),
                                      endDate: form.getFieldValue([
                                        'experiences',
                                        f.name,
                                        'endDate'
                                      ])
                                    }))
                                    return allExperiences.some(
                                      (exp) =>
                                        exp.startDate &&
                                        exp.endDate &&
                                        dayjs(current).isBetween(
                                          exp.startDate,
                                          exp.endDate,
                                          null,
                                          '[]'
                                        )
                                    )
                                  }}
                                />
                              </Form.Item>
                              {!currentJobs[experiences[name]?._id] && (
                                <Form.Item name={[name, 'endDate']} noStyle>
                                  <DatePicker
                                    picker='month'
                                    placeholder='結束時間'
                                    style={{ width: '100px' }}
                                    disabled={
                                      currentJobs[experiences[name]?._id]
                                    }
                                    disabledDate={(current) => {
                                      const startDate = form.getFieldValue([
                                        'experiences',
                                        name,
                                        'startDate'
                                      ])
                                      return (
                                        current &&
                                        dayjs(current).isBefore(
                                          startDate,
                                          'month'
                                        )
                                      )
                                    }}
                                  />
                                </Form.Item>
                              )}
                            </Space.Compact>
                            <Checkbox
                              checked={currentJobs[experiences[name]?._id]}
                              onChange={(e) => {
                                const newCurrentJobs = { ...currentJobs }
                                newCurrentJobs[experiences[name]?._id] =
                                  e.target.checked
                                setCurrentJobs(newCurrentJobs)
                                if (e.target.checked) {
                                  form.setFieldsValue({
                                    experiences: {
                                      [name]: {
                                        endDate: null
                                      }
                                    }
                                  })
                                }
                              }}
                            >
                              至今
                            </Checkbox>
                          </Space>
                        </Form.Item>
                      </Col>
                      <Col xs={20} sm={20} md={6} lg={3}>
                        <Form.Item
                          {...restField}
                          name={[name, 'location']}
                          rules={[
                            { required: true, message: '請輸入工作地點' }
                          ]}
                        >
                          <Input placeholder='工作地點' />
                        </Form.Item>
                      </Col>
                      <Col xs={4} sm={4} md={2} lg={1}>
                        <Form.Item
                          {...restField}
                          name={[name, 'color']}
                          rules={[{ required: true, message: '請選擇顏色' }]}
                        >
                          <ColorPicker />
                        </Form.Item>
                      </Col>
                      <Col xs={24} style={{ textAlign: 'center' }}>
                        <Button
                          type='text'
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        >
                          刪除此工作經歷
                        </Button>
                      </Col>
                    </Row>
                  </div>
                ))}
                <Form.Item>
                  <Button
                    type='dashed'
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    添加工作經歷
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
          <Form.Item>
            <ButtonGroup>
              <StyledButton type='primary' htmlType='submit'>
                保存
              </StyledButton>
            </ButtonGroup>
          </Form.Item>
        </Form>
      )}
    </>
  )
}

export default EmployeeExperience
